import ticketgroupService from '../../services/tickets/ticketgroup.service.js'

import entityUtility from '../../utils/entity.utility.js'
import httpUtility from '../../utils/http.utility.js'

import { useToast } from '../../composables/useToast.js'

import { t } from '@/i18n'

const toast = useToast()

async function getTicketgroup(id) {
  try {
    return await ticketgroupService.get.one(id)
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.loadGroups')))
    return null
  }
}

async function getTicketgroups() {
  try {
    return await ticketgroupService.get.all()
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.loadGroups')))
    return []
  }
}

async function getTicketgroupSettings(id, { silent = false } = {}) {
  try {
    return await ticketgroupService.get.settings(id)
  } catch (err) {
    if (!silent) {
      toast.error(httpUtility.error.message(err, t('tickets.settings.errors.loadConfig')))
    }
    return null
  }
}

async function saveTicketgroup(payload) {
  try {
    let response
    if (payload._id) response = await ticketgroupService.update.ticketgroup(payload._id, payload)
    else response = await ticketgroupService.create.ticketgroup(payload)
    return response.data.ticketGroup
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.settings.errors.saveGroup')))
    return null
  }
}

async function deleteTicketgroup(id) {
  try {
    await ticketgroupService.delete.ticketgroup(id)
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.settings.errors.deleteGroup')))
    return false
  }
}

function mapTicketgroups(array) {
  return entityUtility.map(array, 'name')
}

function findTicketgroup(id, array) {
  return entityUtility.find(id, array)
}

class TicketGroupController {
  get = {
    /**
     * Load one ticket group by id. Returns the group document on
     * success, `null` on failure (toasts the error).
     *
     * @param {string} id
     * @returns {Promise<object|null>}
     */
    async one(id) {
      return getTicketgroup(id)
    },

    /**
     * Load every ticket group visible to the caller. Returns the list
     * on success, `[]` on failure (toasts the error).
     *
     * @returns {Promise<object[]>}
     */
    async all() {
      return getTicketgroups()
    },

    /**
     * Load ticket settings for one group scope. Returns the settings
     * document on success, `null` on failure (toasts unless `silent`).
     *
     * @param {string} id
     * @param {{ silent?: boolean }} [opts]
     * @returns {Promise<object|null>}
     */
    async settings(id, opts) {
      return getTicketgroupSettings(id, opts)
    },
  }

  /**
   * Map each ticket group to `{ value, label }`.
   *
   * @param {object[]} array
   * @returns {Array<{ value: string, label: string }>}
   */
  map(array) {
    return mapTicketgroups(array)
  }

  /**
   * Look up one group by `id` in `array`. Returns the row, or `null`
   * when `id` is missing or not found.
   *
   * @param {string|null|undefined} id
   * @param {Array<{ _id: string }>} array
   * @returns {object|null}
   */
  find(id, array) {
    return findTicketgroup(id, array)
  }

  save = {
    /**
     * Create or update a ticket group. Branches on `payload._id`
     * (truthy → update; omitted → create). Returns the saved group on
     * success, `null` on failure (toasts the error).
     *
     * @param {object} payload
     * @returns {Promise<object|null>}
     */
    async ticketgroup(payload) {
      return saveTicketgroup(payload)
    },
  }

  delete = {
    /**
     * Delete a ticket group. Returns `true` on success, `false` on
     * failure (toasts the error).
     *
     * @param {string} id
     * @returns {Promise<boolean>}
     */
    async ticketgroup(id) {
      return deleteTicketgroup(id)
    },
  }
}

export default new TicketGroupController()
